/**
 * User settings — everything that survives a reload but is not the exercise
 * itself. Kept apart from the session store so the shapes stay obvious and the
 * persistence layer has one small thing to serialise.
 */

import type { AssistId, CfPosition, Edo, RulesProfile } from '../core';
import type { ClickVoice } from '../audio/engine';

/** Which voices the play button sounds. */
export type PlaybackVoices = 'both' | 'cf' | 'cp';

/** Staff engraving layout. */
export type StaffLayout = 'grand' | 'single';

/** Colour scheme, following the system by default. */
export type ThemePreference = 'system' | 'light' | 'dark';

export interface Settings {
  /** Equal division of the octave used when exercises are created. */
  edo: Edo;
  /** Which teacher's conventions the grader enforces. */
  profile: RulesProfile;
  /** Grand staff, or both voices merged onto one. */
  layout: StaffLayout;
  theme: ThemePreference;

  /** Key for new exercises; `'random'` picks one each time. */
  keyPreference: string | 'random';
  /** Which voice the cantus firmus lands in for new exercises. */
  cfPreference: CfPosition | 'random';

  /** Semibreves (whole notes) per minute. */
  tempo: number;
  playbackVoices: PlaybackVoices;
  /** Whether the metronome ticks along with playback. Off by default: the tap
   *  button sounds regardless, but nothing else clicks unless asked. */
  metronomeDuringPlayback: boolean;
  clickVoice: ClickVoice;
  clickVolume: number;
  volume: number;
  /** Sound the interval the moment a note is placed. */
  auditionOnPlace: boolean;

  /** Which live assists are engaged. */
  assists: Record<AssistId, boolean>;
  /** Ask before discarding written work with the dice, and before grading. */
  confirmDestructive: boolean;
}

/** The defaults a first-time visitor gets. */
export const DEFAULT_SETTINGS: Settings = {
  edo: 12,
  profile: 'lim',
  layout: 'single',
  theme: 'system',
  keyPreference: 'random',
  cfPreference: 'random',
  tempo: 40,
  playbackVoices: 'both',
  metronomeDuringPlayback: false,
  clickVoice: 'wood',
  clickVolume: 0.6,
  volume: 0.9,
  auditionOnPlace: true,
  assists: { labels: true, allowed: true },
  confirmDestructive: true,
};

/** Merge a stored blob over the defaults, dropping anything unrecognised. */
export function coerceSettings(raw: unknown): Settings {
  const r = (raw ?? {}) as Partial<Settings>;
  const pick = <K extends keyof Settings>(k: K, ok: (v: any) => boolean): Settings[K] =>
    ok(r[k]) ? (r[k] as Settings[K]) : DEFAULT_SETTINGS[k];

  return {
    edo: pick('edo', (v) => v === 12 || v === 31),
    profile: pick('profile', (v) => v === 'lim' || v === 'gran'),
    layout: pick('layout', (v) => v === 'grand' || v === 'single'),
    theme: pick('theme', (v) => v === 'system' || v === 'light' || v === 'dark'),
    keyPreference: pick('keyPreference', (v) => typeof v === 'string'),
    cfPreference: pick('cfPreference', (v) => v === 'random' || v === 'lower' || v === 'upper'),
    tempo: pick('tempo', (v) => typeof v === 'number' && v >= 20 && v <= 100),
    playbackVoices: pick('playbackVoices', (v) => v === 'both' || v === 'cf' || v === 'cp'),
    metronomeDuringPlayback: pick('metronomeDuringPlayback', (v) => typeof v === 'boolean'),
    clickVoice: pick('clickVoice', (v) => v === 'wood' || v === 'beep' || v === 'rim'),
    clickVolume: pick('clickVolume', (v) => typeof v === 'number' && v >= 0 && v <= 1),
    volume: pick('volume', (v) => typeof v === 'number' && v >= 0 && v <= 1),
    auditionOnPlace: pick('auditionOnPlace', (v) => typeof v === 'boolean'),
    assists: {
      labels: typeof r.assists?.labels === 'boolean' ? r.assists.labels : DEFAULT_SETTINGS.assists.labels,
      allowed: typeof r.assists?.allowed === 'boolean' ? r.assists.allowed : DEFAULT_SETTINGS.assists.allowed,
    },
    confirmDestructive: pick('confirmDestructive', (v) => typeof v === 'boolean'),
  };
}
